import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import PasswordStrength, { scorePassword } from "@/components/PasswordStrength";
import { toast } from "sonner";
import AuthPromo from "@/components/AuthPromo";
import { ArrowRight, Loader2, KeyRound, ShieldCheck } from "lucide-react";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/fd1c566f5b8864f6832e4d35cf9140f3939f77c249aa2aa7f7d74ab738a59602.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!token) { toast.error("Missing reset token."); return; }
    const s = scorePassword(password);
    if (s.score < 2) { toast.error("Please choose a stronger password."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login"), 800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-[#F0ECE0] flex flex-col">
        <div className="px-8 md:px-14 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="reset-logo">
            <Logo variant="wordmark" size={36} />
          </Link>
          <Link to="/login" className="text-sm text-[#4F4D45] hover:text-[#1B3526]" data-testid="reset-link-login">Sign in instead</Link>
        </div>
        <div className="flex-1 px-8 md:px-14 pt-16 pb-10 flex flex-col">
          <form onSubmit={submit} className="max-w-md w-full">
            <div className="w-12 h-12 rounded-xl bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center mb-6">
              <KeyRound className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Set a new password</h2>
            <p className="text-[#4F4D45] mt-3">Choose something memorable — and strong. Your account is protected with bcrypt hashing on every save.</p>

            {!token && (
              <div className="mt-5 p-3 rounded-lg bg-[#992F2F]/10 border border-[#992F2F]/40 text-sm text-[#992F2F]" data-testid="reset-missing-token">
                This reset link is missing a token. Please request a new link.
              </div>
            )}

            <div className="mt-8 space-y-4">
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">New password</Label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" testId="reset-input-password" className="mt-1" />
                <PasswordStrength value={password} />
              </div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Confirm new password</Label>
                <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password" testId="reset-input-confirm" className="mt-1" />
                {confirm && confirm !== password && (
                  <div className="text-xs text-[#992F2F] mt-1.5" data-testid="reset-password-mismatch">Passwords don't match.</div>
                )}
              </div>
            </div>

            <Button type="submit" disabled={loading || !token || done} className="mt-8 bg-[#1B3526] hover:bg-[#0F2517] w-full sm:w-auto" data-testid="reset-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update password <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
            <p className="text-xs text-[#4F4D45] mt-8 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#1B3526]" /> After update, all existing sessions for this account remain valid — sign in fresh to revoke older ones.</p>
          </form>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <AuthPromo />
      </div>
    </div>
  );
}
